/** @jsxImportSource theme-ui */
import React, { useState, Suspense, lazy } from "react"
import { useTranslation } from "react-i18next"
import { useMutation } from "react-relay"

import TextField from "../../Radiate/TextField/TextField"
import BoxButton from "../../Radiate/NewBoxButton/NewBoxButton"
import { toast } from "../../Radiate/Toaster/Toaster"
import {
  CreateOutboundHookMutation,
  UpdateOutboundHookMutation,
  DeleteOutboundHookMutation,
} from "../SettingsModalQuery"
import { isConflict, errorMessage, networkErrorMessage } from "./hookErrors"
import * as Styles from "./styles"

// Lazy-load the Ace editor so react-ace/brace (~500 KB) only loads when an
// admin actually opens a hook editor, keeping it out of the main bundle.
const CodeEditor = lazy(() => import("../../Radiate/CodeEditor/CodeEditor"))

const formatTime = (ms) => {
  if (!ms) return ""
  try {
    return new Date(Number(ms)).toLocaleString()
  } catch (e) {
    return ""
  }
}

const HookRow = ({
  hook,
  isNew = false,
  wAppId,
  appRelayId,
  canEdit,
  expandedByDefault = false,
  onConflict,
  onCreated,
  onDeleted,
}) => {
  const { t } = useTranslation(["settings", "common"])

  const [createHook, creating] = useMutation(CreateOutboundHookMutation)
  const [updateHook, updating] = useMutation(UpdateOutboundHookMutation)
  const [deleteHook, deleting] = useMutation(DeleteOutboundHookMutation)

  const [isOpen, setIsOpen] = useState(isNew || expandedByDefault)
  const [name, setName] = useState(hook?.name || "")
  const [description, setDescription] = useState(hook?.description || "")
  const [code, setCode] = useState(hook?.code || "")
  const [error, setError] = useState("")

  // code === null means the API redacted it (viewer is not an admin).
  const codeRedacted = !isNew && hook?.code == null
  const editable = canEdit && !codeRedacted

  const dirty = isNew
    ? name.trim().length > 0 && code.trim().length > 0
    : name !== (hook?.name || "") ||
      description !== (hook?.description || "") ||
      code !== (hook?.code || "")

  const inFlight = creating || updating

  const handleCreate = () => {
    setError("")
    let clientMutationId = 0
    createHook({
      variables: {
        input: { wAppId, name, description, code, clientMutationId: clientMutationId++ },
      },
      updater: (store) => {
        const payload = store.getRootField("createOutboundHook")
        const created = payload?.getLinkedRecord("hook")
        const app = appRelayId ? store.get(appRelayId) : null
        if (!created || !app) return
        const existing = app.getLinkedRecords("outboundHooks") || []
        if (existing.some((r) => r.getDataID() === created.getDataID())) return
        app.setLinkedRecords([...existing, created], "outboundHooks")
      },
      onCompleted: (response) => {
        const err = response?.createOutboundHook?.error
        if (err) {
          setError(errorMessage(err, t("common:errors.something_went_wrong", "Something went wrong.")))
          return
        }
        toast.success(t("outbound_hooks.toast.created", "Hook created"))
        setName("")
        setDescription("")
        setCode("")
        if (onCreated) onCreated()
      },
      onError: (e) => {
        setError(networkErrorMessage(e, t("common:errors.something_went_wrong", "Something went wrong.")))
      },
    })
  }

  const handleUpdate = () => {
    setError("")
    let clientMutationId = 0
    updateHook({
      variables: {
        input: { hookId: hook._id, etag: hook.etag, name, description, code, clientMutationId: clientMutationId++ },
      },
      onCompleted: (response) => {
        const err = response?.updateOutboundHook?.error
        if (err) {
          if (isConflict(err)) {
            if (onConflict) onConflict()
            return
          }
          setError(errorMessage(err, t("common:errors.something_went_wrong", "Something went wrong.")))
          return
        }
        toast.success(t("outbound_hooks.toast.updated", "Hook saved"))
        setIsOpen(false)
      },
      onError: (e) => {
        setError(networkErrorMessage(e, t("common:errors.something_went_wrong", "Something went wrong.")))
      },
    })
  }

  const handleDelete = () => {
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      t(
        "outbound_hooks.editor.delete_confirm",
        "Delete this hook? It will be removed from any channels it is attached to. This cannot be undone."
      )
    )
    if (!confirmed) return
    setError("")
    let clientMutationId = 0
    const deletedDataId = hook.id
    deleteHook({
      variables: { input: { hookId: hook._id, clientMutationId: clientMutationId++ } },
      updater: (store) => {
        const app = appRelayId ? store.get(appRelayId) : null
        if (!app) return
        const existing = app.getLinkedRecords("outboundHooks") || []
        app.setLinkedRecords(
          existing.filter((r) => r.getDataID() !== deletedDataId),
          "outboundHooks"
        )
      },
      onCompleted: (response) => {
        const err = response?.deleteOutboundHook?.error
        if (err) {
          if (isConflict(err) && onConflict) {
            onConflict()
            return
          }
          setError(errorMessage(err, t("common:errors.something_went_wrong", "Something went wrong.")))
          return
        }
        toast.success(t("outbound_hooks.toast.deleted", "Hook deleted"))
        if (onDeleted) onDeleted()
      },
      onError: (e) => {
        setError(networkErrorMessage(e, t("common:errors.something_went_wrong", "Something went wrong.")))
      },
    })
  }

  const editorBody = (
    <Styles.HookEditorBody>
      <Styles.FieldRow>
        <TextField
          useInput
          borderStyle="M"
          label={t("outbound_hooks.editor.name", "Name")}
          value={name}
          disabled={!editable}
          maxLength={120}
          hideLengthCounter
          onChange={(e) => setName(e.target.value)}
        />
        <TextField
          useInput
          borderStyle="M"
          label={t("outbound_hooks.editor.description", "Description")}
          value={description}
          disabled={!editable}
          maxLength={280}
          hideLengthCounter
          onChange={(e) => setDescription(e.target.value)}
        />
      </Styles.FieldRow>

      <div sx={{ fontSize: "14px", color: "gray9", mt: 2, mb: 1 }}>
        {t("outbound_hooks.editor.code", "Function code")}
      </div>
      {codeRedacted ? (
        <Styles.ReadOnlyNotice>
          {t(
            "outbound_hooks.editor.code_redacted",
            "Hook code is hidden — administrator permission is required to view or edit it."
          )}
        </Styles.ReadOnlyNotice>
      ) : (
        <Suspense
          fallback={
            <Styles.ReadOnlyNotice>
              {t("outbound_hooks.editor.loading", "Loading editor…")}
            </Styles.ReadOnlyNotice>
          }
        >
          <CodeEditor
            id={`hook-code-${hook?.id || "new"}`}
            mode="jsx"
            height={260}
            value={code}
            readOnly={!editable}
            onChange={(value) => setCode(value)}
            sx={{
              zIndex: 0,
            }}
          />
        </Suspense>
      )}

      {!editable && !codeRedacted && (
        <Styles.ReadOnlyNotice>
          {t("outbound_hooks.editor.read_only_admin", "Read-only — administrator permission required to edit.")}
        </Styles.ReadOnlyNotice>
      )}

      {error && <Styles.InlineError>{error}</Styles.InlineError>}

      {editable && (
        <Styles.EditorToolbar>
          <div>
            {!isNew && (
              <BoxButton
                danger
                borderless
                icon="trash"
                text={t("common:buttons.delete", "Delete")}
                loading={deleting}
                onClick={handleDelete}
              />
            )}
          </div>
          <BoxButton
            primary
            text={isNew ? t("common:buttons.create", "Create") : t("common:buttons.save", "Save")}
            loading={inFlight}
            disabled={!dirty || inFlight}
            onClick={isNew ? handleCreate : handleUpdate}
          />
        </Styles.EditorToolbar>
      )}
    </Styles.HookEditorBody>
  )

  if (isNew) {
    return <Styles.HookCard>{editorBody}</Styles.HookCard>
  }

  return (
    <Styles.HookCard>
      <Styles.HookSummary onClick={() => setIsOpen((v) => !v)}>
        <Styles.HookSummaryMain>
          <Styles.HookName>{hook?.name}</Styles.HookName>
          <Styles.HookMeta>
            {hook?.description ||
              t("outbound_hooks.no_description", "No description")}
            {hook?.updatedAt
              ? ` · ${t("outbound_hooks.updated", "Updated")} ${formatTime(hook.updatedAt)}`
              : ""}
          </Styles.HookMeta>
        </Styles.HookSummaryMain>
        <BoxButton icon={isOpen ? "chevron-down" : "chevron-right"} borderless />
      </Styles.HookSummary>
      {isOpen && editorBody}
    </Styles.HookCard>
  )
}

export default HookRow
